import { Component, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputAutoResizeDirective } from '../../../../utils/input-auto-resize.directive';
import { ChatService, ChatMessage } from '../chat.service';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';

interface SelectedFile {
  file: File;
  preview?: string;
  type: 'image' | 'file';
}

@Component({
  selector: 'app-chat-input-actions',
  standalone: true,
  imports: [CommonModule, InputAutoResizeDirective],
  templateUrl: './chat-input-actions.component.html',
  styleUrl: './chat-input-actions.component.css'
})
export class ChatInputActionsComponent {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('emojiModal') emojiModal!: ElementRef<HTMLDivElement>;

  selectedFiles: SelectedFile[] = [];
  showEmojiPicker = false;
  replyToMessage: ChatMessage | null = null;
  isSending = false;
  maxMessageLength = 1000;

  // Audio recording state
  isRecording = false;
  recordingTime = 0;
  recordingInterval: any;
  mediaRecorder: MediaRecorder | null = null;
  audioStream: MediaStream | null = null;

  constructor(private chatService: ChatService) {}

  async sendMessage() {
    if (this.isSending) {
      return;
    }

    let message = this.messageInput.nativeElement.value.trim();
    if (message.length > this.maxMessageLength) {
      message = message.slice(0, this.maxMessageLength);
    }
    if (!message && !this.selectedFiles.length) {
      return;
    }

    try {
      this.isSending = true;
      this.messageInput.nativeElement.value = '';
      this.adjustTextareaHeight();

      // Send text message if exists
      console.log('Sending message:', message);
      if (message) {
        await this.chatService.sendMessage(message, 'text');
        this.clearReply();
      }

      // Send all selected files
      for (const selectedFile of this.selectedFiles) {
        if (selectedFile.type === 'image') {
          // Upload to Firebase and send as image message
          const fileUrl = await this.chatService.uploadFile(selectedFile.file);
          await this.chatService.sendMessage(`📷 ${selectedFile.file.name}`, 'image', { url: fileUrl, name: selectedFile.file.name });
        } else {
          // Upload to Firebase and send as file message
          const fileUrl = await this.chatService.uploadFile(selectedFile.file);
          await this.chatService.sendMessage(`📎 ${selectedFile.file.name}`, 'file', { url: fileUrl, name: selectedFile.file.name });
        }
      }

      // Clear all selected files after sending
      this.clearSelectedFiles();

      // Scroll to bottom after sending message
      setTimeout(() => this.scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      if (message) {
        this.messageInput.nativeElement.value = message;
        this.adjustTextareaHeight();
      }
      // TODO: Show error toast to user
    } finally {
      this.isSending = false;
    }
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onInput() {
    const textarea = this.messageInput.nativeElement;
    if (textarea.value.length > this.maxMessageLength) {
      textarea.value = textarea.value.slice(0, this.maxMessageLength);
      textarea.selectionStart = textarea.selectionEnd = this.maxMessageLength;
    }
    this.adjustTextareaHeight();
  }

  private adjustTextareaHeight() {
    const textarea = this.messageInput.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  onEmojiSelect(event: any) {
    this.insertEmoji(event.emoji.native);
    // Don't close the picker to allow multiple emoji selection
  }

  private insertEmoji(emoji: string) {
    const textarea = this.messageInput.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    const newValue = before + emoji + after;
    if (newValue.length > this.maxMessageLength) {
      textarea.value = newValue.slice(0, this.maxMessageLength);
      textarea.selectionStart = textarea.selectionEnd = this.maxMessageLength;
    } else {
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
    }
    textarea.focus();
    this.adjustTextareaHeight();
  }

  openImagePicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true; // Allow multiple file selection
    input.onchange = (event: any) => {
      const files = Array.from(event.target.files) as File[];
      files.forEach(file => {
        if (file) {
          this.selectFile(file, 'image');
        }
      });
    };
    input.click();
  }

  openAttachmentPicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true; // Allow multiple file selection
    input.onchange = (event: any) => {
      const files = Array.from(event.target.files) as File[];
      files.forEach(file => {
        if (file) {
          this.selectFile(file, 'file');
        }
      });
    };
    input.click();
  }

  private selectFile(file: File, type: 'image' | 'file') {
    // Check if file already exists
    const existingIndex = this.selectedFiles.findIndex(f => f.file.name === file.name && f.file.size === file.size);
    if (existingIndex !== -1) return; // Skip duplicate files

    // Create preview for images
    if (type === 'image') {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedFiles.push({
          file: file,
          preview: e.target.result,
          type: type
        });
      };
      reader.readAsDataURL(file);
    } else {
      this.selectedFiles.push({
        file: file,
        type: type
      });
    }
  }

  removeSelectedFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  private clearSelectedFiles() {
    this.selectedFiles = [];
  }

  clearReply() {
    this.replyToMessage = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.showEmojiPicker && this.emojiModal) {
      const clickedInside = this.emojiModal.nativeElement.contains(event.target as Node);
      if (!clickedInside) {
        this.showEmojiPicker = false;
      }
    }
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📽️';
      case 'zip':
      case 'rar': return '📦';
      case 'txt': return '📄';
      default: return '📎';
    }
  }

  private scrollToBottom() {
    const chatContainer = document.querySelector('.h-full.overflow-y-auto.relative');
    if (chatContainer) {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  }
}
